(() => {
  'use strict';
  if (customElements.get('ag-series-selector')) return;
  const normal = (value) => String(value ?? '').normalize('NFKC').trim().toLowerCase();
  const text = {
    pl: {heading:'Inne modele z tej serii',note:'Najedź na miniaturę, aby zobaczyć podgląd.',more:'Pokaż wszystkie ({count})',less:'Pokaż mniej'},
    en: {heading:'Other models in this series',note:'Hover a thumbnail to preview it.',more:'Show all ({count})',less:'Show less'},
    de: {heading:'Weitere Modelle dieser Serie',note:'Für eine Vorschau über das Bild fahren.',more:'Alle anzeigen ({count})',less:'Weniger anzeigen'}
  };
  async function fetchText(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {credentials:'same-origin',signal:controller.signal,cache:'no-cache'});
      if (!response.ok) throw new Error(`Series request: ${response.status}`);
      return await response.text();
    } finally { clearTimeout(timer); }
  }
  const safeProductUrl = (value) => {
    const url = new URL(value,location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
    return url;
  };
  class SeriesSelector extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.previewTimer = null;
      this.previewOverlay = null;
      this.previewCard = null;
      this.load().catch((error) => {
        this.hidden = true;
        if (this.dataset.designMode === 'true') console.warn('[Product series]',error);
      });
    }
    disconnectedCallback() { this.cancelPreview(); }
    async load() {
      const current = JSON.parse(this.querySelector('[data-ag-series-current]').textContent);
      const series = String(current.series ?? '').trim();
      if (!series) return;
      const url = new URL(this.dataset.collectionUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid collection URL');
      url.searchParams.set('filter.p.m.custom.seria', series);
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.set('section_id', 'ag-series-metafield-results');
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);
      const products = new Map();
      let page = 1;
      while (page) {
        if (page > 5) throw new Error('Series exceeds pagination limit');
        url.searchParams.set('page', String(page));
        const html = await fetchText(url);
        const documentResult = new DOMParser().parseFromString(html, 'text/html');
        const payload = documentResult.querySelector('[data-ag-series-results]');
        if (!payload) throw new Error('Series response is missing');
        const result = JSON.parse(payload.textContent);
        if (result.filterConfigured !== true || result.series !== series || !Array.isArray(result.products)) {
          throw new Error('Series filter is not configured');
        }
        for (const product of result.products) {
          if (product.series !== series) throw new Error('Mixed series response');
          if (product.available && product.series === series && String(product.id) !== String(current.id)) {
            safeProductUrl(product.url);
            products.set(String(product.id), product);
          }
        }
        if (result.next !== null && result.next !== page + 1) throw new Error('Invalid series pagination');
        page = result.next;
      }
      const alternatives = Array.from(products.values()).sort((a, b) => String(a.sku).localeCompare(String(b.sku), 'pl', {numeric:true}));
      if (!alternatives.length || !this.isConnected) return;
      this.render(current, alternatives, previewTheme);
    }
    getPreviewTarget() {
      if (!matchMedia('(min-width:577px)').matches) return null;
      const desktopGallery = document.querySelector('.product--thumbnail_slider');
      if (!desktopGallery) return null;
      return desktopGallery.querySelector('swiper-slider .swiper-slide.swiper-slide-active .product-media__item')
        || desktopGallery.querySelector('swiper-slider .swiper-slide .product-media__item');
    }
    showPreview(product,card) {
      if (this.previewCard !== card) return;
      const previewUrl = product.preview || product.image;
      if (!previewUrl) return;
      const target = this.getPreviewTarget();
      if (!target) return;
      this.restorePreview();
      const overlay = document.createElement('img');
      overlay.className = 'ag-series-preview-overlay';
      overlay.src = previewUrl;
      overlay.alt = product.title || '';
      overlay.decoding = 'async';
      target.append(overlay);
      this.previewOverlay = overlay;
    }
    schedulePreview(product,card) {
      if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
      this.cancelPreview(false);
      this.previewCard = card;
      const previewUrl = product.preview || product.image;
      if (!previewUrl) return;
      this.previewTimer = window.setTimeout(() => {
        const image = new Image();
        image.onload = () => {
          if (this.previewCard === card) this.showPreview(product,card);
        };
        image.src = previewUrl;
      },120);
    }
    restorePreview() {
      if (this.previewOverlay) {
        this.previewOverlay.remove();
        this.previewOverlay = null;
      }
    }
    cancelPreview(clearCard = true) {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
      this.restorePreview();
      if (clearCard) this.previewCard = null;
    }
    render(current,alternatives,previewTheme) {
      const lang = normal(this.dataset.locale).split('-')[0];
      const labels = text[lang] || text.en;
      const heading = document.createElement('h2');
      heading.className = 'ag-series__heading';
      heading.id = `${this.id}-heading`;
      heading.textContent = labels.heading;
      const note = document.createElement('p');
      note.className = 'ag-series__note';
      note.textContent = labels.note;
      const list = document.createElement('ul');
      list.className = 'ag-series__list';
      list.id = `${this.id}-list`;
      const cards = [current,...alternatives];
      const hiddenItems = [];
      const previewCount = matchMedia('(max-width:575px)').matches ? 4 : 6;
      cards.forEach((product,index) => {
        const li = document.createElement('li');
        li.className = 'ag-series__item';
        if (index >= previewCount) {li.hidden = true;hiddenItems.push(li);}
        const card = document.createElement(index === 0 ? 'span' : 'a');
        card.className = 'ag-series__card';
        card.title = product.title;
        card.setAttribute('aria-label',product.title);
        if (index === 0) {
          card.setAttribute('aria-current','page');
        } else {
          const url = safeProductUrl(product.url);
          if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id',previewTheme);
          card.href = url.pathname + url.search;
          card.addEventListener('mouseenter',() => this.schedulePreview(product,card));
          card.addEventListener('mouseleave',() => this.cancelPreview());
          card.addEventListener('focus',() => this.schedulePreview(product,card));
          card.addEventListener('blur',() => this.cancelPreview());
        }
        const media = document.createElement('span');
        media.className = 'ag-series__media';
        if (product.image) {
          const imageUrl = new URL(product.image,location.origin);
          if (imageUrl.protocol === 'https:') {
            const image = document.createElement('img');
            image.src = imageUrl.href;
            image.alt = '';
            image.width = 120;
            image.height = 120;
            image.loading = 'lazy';
            image.decoding = 'async';
            media.append(image);
          }
        }
        const price = document.createElement('span');
        price.className = 'ag-series__price';
        price.textContent = product.price || '';
        card.append(media,price);
        li.append(card);
        list.append(li);
      });
      this.setAttribute('role','region');
      this.setAttribute('aria-labelledby',heading.id);
      this.replaceChildren(heading,note,list);
      if (hiddenItems.length) {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'ag-series__more';
        toggle.setAttribute('aria-controls',list.id);
        toggle.setAttribute('aria-expanded','false');
        const more = labels.more.replace('{count}',String(cards.length));
        toggle.textContent = more;
        toggle.addEventListener('click',() => {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          hiddenItems.forEach((item) => {item.hidden = !expanded;});
          toggle.setAttribute('aria-expanded',String(expanded));
          toggle.textContent = expanded ? labels.less : more;
        });
        this.append(toggle);
      }
      this.hidden = false;
      this.dataset.loaded = 'true';
    }
  }
  customElements.define('ag-series-selector',SeriesSelector);
})();